import React from 'react';
import { Box, Typography } from '@mui/material';

export interface BoxPlotDataPoint {
  name: string;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  mean: number;
  stdDev: number;
}

interface BoxPlotChartProps {
  data: BoxPlotDataPoint[];
  width?: number | string;
  height?: number;
  yAxisLabel?: string;
  xAxisLabel?: string;
  valueFormatter?: (value: number) => string;
  tooltipUnit?: string;
  boxFill?: string;
  boxHoverFill?: string;
  boxStroke?: string;
}

const BoxPlotChart: React.FC<BoxPlotChartProps> = ({ 
  data, 
  width = '100%', 
  height = 450,
  yAxisLabel = 'Total Energy (J)',
  xAxisLabel = 'Methods',
  valueFormatter = (value) => value.toFixed(2),
  tooltipUnit = '',
  boxFill = '#D4C4A8',
  boxHoverFill = '#C4B49A',
  boxStroke = '#8B7355',
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(800);
  
  // Track the actual rendered width so the chart responds to dialog maximize/restore.
  React.useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(containerRef.current);
      window.addEventListener('resize', updateWidth);

      return () => {
        observer.disconnect();
        window.removeEventListener('resize', updateWidth);
      };
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  
  const margin = { top: 30, right: 20, bottom: 80, left: 60 };
  
  // Find min and max values for Y-axis scaling
  const allValues = data.flatMap(d => [d.min, d.max]);
  const yMin = Math.min(...allValues);
  const yMax = Math.max(...allValues);
  const yRange = yMax - yMin;
  const yPadding = yRange * 0.1;
  const yScaleMin = Math.max(0, yMin - yPadding);
  const yScaleMax = yMax + yPadding;
  
  // Calculate dimensions based on actual container width
  const chartWidth = typeof width === 'number' ? width : containerWidth;
  const chartHeight = height;
  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;
  
  // Box width
  const boxWidth = Math.min(80, plotWidth / (data.length * 2));
  const spacing = plotWidth / data.length;
  
  // Scale function for Y-axis
  const scaleY = (value: number) => {
    const normalized = (value - yScaleMin) / (yScaleMax - yScaleMin);
    return plotHeight - (normalized * plotHeight);
  };
  
  // Generate Y-axis ticks
  const numTicks = 6;
  const tickInterval = (yScaleMax - yScaleMin) / (numTicks - 1);
  const yTicks = Array.from({ length: numTicks }, (_, i) => 
    yScaleMin + (i * tickInterval)
  );
  
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  
  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: width, height: height, overflow: 'hidden' }}>
      <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        {/* Grid lines */}
        {yTicks.map((tick, i) => (
          <g key={`grid-${i}`}>
            <line
              x1={margin.left}
              y1={margin.top + scaleY(tick)}
              x2={margin.left + plotWidth}
              y2={margin.top + scaleY(tick)}
              stroke="#e0e0e0"
              strokeDasharray="3,3"
            />
          </g>
        ))}
        
        {/* Y-axis */}
        <line
          x1={margin.left}
          y1={margin.top}
          x2={margin.left}
          y2={margin.top + plotHeight}
          stroke="#666"
          strokeWidth={2}
        />
        
        {/* Y-axis ticks and labels */}
        {yTicks.map((tick, i) => (
          <g key={`ytick-${i}`}>
            <line
              x1={margin.left - 5}
              y1={margin.top + scaleY(tick)}
              x2={margin.left}
              y2={margin.top + scaleY(tick)}
              stroke="#666"
              strokeWidth={2}
            />
            <text
              x={margin.left - 10}
              y={margin.top + scaleY(tick)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="12"
              fill="#666"
            >
              {valueFormatter(tick)}
            </text>
          </g>
        ))}
        
        {/* Y-axis label */}
        <text
          x={15}
          y={margin.top + plotHeight / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="14"
          fill="#666"
          transform={`rotate(-90, 15, ${margin.top + plotHeight / 2})`}
          fontWeight="600"
        >
          {yAxisLabel}
        </text>
        
        {/* X-axis */}
        <line
          x1={margin.left}
          y1={margin.top + plotHeight}
          x2={margin.left + plotWidth}
          y2={margin.top + plotHeight}
          stroke="#666"
          strokeWidth={2}
        />
        
        {/* Box plots */}
        {data.map((d, index) => {
          const x = margin.left + spacing * index + spacing / 2;
          const isHovered = hoveredIndex === index;
          
          return (
            <g 
              key={d.name}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{ cursor: 'pointer' }}
            >
              {/* Lower whisker (min to Q1) */}
              <line
                x1={x}
                y1={margin.top + scaleY(d.min)}
                x2={x}
                y2={margin.top + scaleY(d.q1)}
                stroke="#666"
                strokeWidth={2}
              />
              
              {/* Min cap */}
              <line
                x1={x - boxWidth / 4}
                y1={margin.top + scaleY(d.min)}
                x2={x + boxWidth / 4}
                y2={margin.top + scaleY(d.min)}
                stroke="#666"
                strokeWidth={2}
              />
              
              {/* Box (Q1 to Q3) */}
              <rect
                x={x - boxWidth / 2}
                y={margin.top + scaleY(d.q3)}
                width={boxWidth}
                height={scaleY(d.q1) - scaleY(d.q3)}
                fill={isHovered ? boxHoverFill : boxFill}
                stroke={boxStroke}
                strokeWidth={2}
              />
              
              {/* Median line */}
              <line
                x1={x - boxWidth / 2}
                y1={margin.top + scaleY(d.median)}
                x2={x + boxWidth / 2}
                y2={margin.top + scaleY(d.median)}
                stroke="#000"
                strokeWidth={3}
              />
              
              {/* Upper whisker (Q3 to max) */}
              <line
                x1={x}
                y1={margin.top + scaleY(d.q3)}
                x2={x}
                y2={margin.top + scaleY(d.max)}
                stroke="#666"
                strokeWidth={2}
              />
              
              {/* Max cap */}
              <line
                x1={x - boxWidth / 4}
                y1={margin.top + scaleY(d.max)}
                x2={x + boxWidth / 4}
                y2={margin.top + scaleY(d.max)}
                stroke="#666"
                strokeWidth={2}
              />
              
              {/* X-axis label */}
              <text
                x={x}
                y={margin.top + plotHeight + 20}
                textAnchor="middle"
                fontSize="14"
                fill="#333"
                fontWeight={isHovered ? "700" : "500"}
              >
                {d.name}
              </text>
              
              {/* Hover area (invisible rect for better hover detection) */}
              <rect
                x={x - spacing / 2}
                y={margin.top}
                width={spacing}
                height={plotHeight}
                fill="transparent"
              />
            </g>
          );
        })}
        
        {/* X-axis label */}
        <text
          x={margin.left + plotWidth / 2}
          y={chartHeight - 20}
          textAnchor="middle"
          fontSize="14"
          fill="#666"
          fontWeight="600"
        >
          {xAxisLabel}
        </text>
      </svg>
      
      {/* Tooltip */}
      {hoveredIndex !== null && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            bgcolor: 'background.paper',
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 3,
            minWidth: 180,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {data[hoveredIndex].name}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            Max: {valueFormatter(data[hoveredIndex].max)}{tooltipUnit}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            Q3 (75%): {valueFormatter(data[hoveredIndex].q3)}{tooltipUnit}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'primary.main' }}>
            Median: {valueFormatter(data[hoveredIndex].median)}{tooltipUnit}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            Q1 (25%): {valueFormatter(data[hoveredIndex].q1)}{tooltipUnit}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            Min: {valueFormatter(data[hoveredIndex].min)}{tooltipUnit}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 0.5, fontStyle: 'italic' }}>
            Mean: {valueFormatter(data[hoveredIndex].mean)}{tooltipUnit} (±{valueFormatter(data[hoveredIndex].stdDev)})
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BoxPlotChart;
